import { useGet, usePatch, usePost, usePut } from "@albertine/api-client";
import type { AgentListItem } from "@albertine/types/openapi/lmt";
import {
    type QueryClient,
    type UseMutationOptions,
    useQueryClient,
} from "@tanstack/react-query";
import { useMemo } from "react";
import type { Agent } from "../types/Agent";

const GET_ALL_AGENTS = "getAllAgents" as const;

type AgentMutationOpts = Pick<
    UseMutationOptions<unknown, unknown, unknown>,
    "onSuccess" | "onError" | "onSettled" | "onMutate"
>;

function agentListItemToAgent(item: AgentListItem): Agent {
    return {
        id: item.id,
        name: item.name,
        firstName: item.firstName,
        email: item.email,
        role: item.role,
        disabled: item.disabled,
        createdAt: item.createdAt,
        permissionGroup: item.permissionGroup ?? null,
        avatarFullPath: item.avatarFullPath ?? null,
        avatarThumbnailPath: item.avatarThumbnailPath ?? null,
        slackId: item.slackId ?? null,
        onboardingCallReservationUrl: item.onboardingCallReservationUrl,
        authProvider: item.authProvider ?? null,
    };
}

function withAgentsInvalidation(
    queryClient: QueryClient,
    opts?: AgentMutationOpts,
): AgentMutationOpts {
    const { onSuccess, ...rest } = opts ?? {};
    return {
        ...rest,
        onSuccess: (data, variables, context) => {
            void queryClient.invalidateQueries({ queryKey: [GET_ALL_AGENTS] });
            onSuccess?.(data, variables, context);
        },
    };
}

function useAgentPost(
    entity: "agentCreateAgent" | "enableAgent" | "disableAgent",
    opts?: AgentMutationOpts,
) {
    const queryClient = useQueryClient();
    return usePost(entity, withAgentsInvalidation(queryClient, opts));
}

function useAgentPatch(
    entity: "updateAgentPermissionGroup" | "updateAgentProfile",
    opts?: AgentMutationOpts,
) {
    const queryClient = useQueryClient();
    return usePatch(entity, withAgentsInvalidation(queryClient, opts));
}

export function useAgents(): Agent[] {
    const { data } = useGet(
        GET_ALL_AGENTS,
        {},
        {
            select: (data) => data.results.map(agentListItemToAgent),
        },
    );
    return data ?? [];
}

export function useAgentsMap(): Record<string, Agent> {
    const agents = useAgents();
    return useMemo(
        () =>
            agents.reduce<Record<string, Agent>>((map, agent) => {
                map[agent.id] = agent;
                return map;
            }, {}),
        [agents],
    );
}

export function useAgent(agentId: string | undefined): Agent | undefined {
    const agentsMap = useAgentsMap();
    if (!agentId) {
        return undefined;
    }
    return agentsMap[agentId];
}

export function useCreateAgent(opts?: AgentMutationOpts) {
    return useAgentPost("agentCreateAgent", opts);
}

export function useEnableAgent(opts?: AgentMutationOpts) {
    return useAgentPost("enableAgent", opts);
}

export function useDisableAgent(opts?: AgentMutationOpts) {
    return useAgentPost("disableAgent", opts);
}

export function useUpdateAgent(opts?: AgentMutationOpts) {
    const queryClient = useQueryClient();
    return usePut("updateAgent", withAgentsInvalidation(queryClient, opts));
}

export function useUpdateAgentPermissionGroup(opts?: AgentMutationOpts) {
    return useAgentPatch("updateAgentPermissionGroup", opts);
}

export function useUpdateAgentProfile(opts?: AgentMutationOpts) {
    return useAgentPatch("updateAgentProfile", opts);
}
